import { useState, useEffect } from 'react';
import { useCart } from '../../../../contexts/CartContext';

export interface ProductVariant {
  id: string;
  size: string;
  price: number;
  compareAtPrice?: number;
  pricePerMeter: number;
  rollLength: number;
  variantImage?: string;
  variantImages?: string[];
  attributes?: Record<string, any>;
  quantity?: number;
  variantTitle?: string;
}

export interface HierarchicalVariant {
  variantLevels: {
    level1: string;
    level2: string;
    level3: string;
  };
  attributes: Record<string, string>;
  sku: string;
  price: number;
  basePrice: number;
  quantity: number;
  isDefault: boolean;
  variantImages: string[];
  variantTitle?: string;
  variantDescription?: string;
  available?: boolean;
}

export interface UseVariantSheetProps {
  product: any;
  variants: ProductVariant[];
  hierarchicalVariants?: HierarchicalVariant[];
  variantHierarchy?: string[];
  onVariantSelect?: (variant: any) => void;
}

export function useVariantSheet({
  product,
  variants,
  hierarchicalVariants,
  variantHierarchy,
  onVariantSelect,
}: UseVariantSheetProps) {
  const hasHierarchicalVariants = hierarchicalVariants && hierarchicalVariants.length > 0 && variantHierarchy;
  const isNonVariantProduct = !hasHierarchicalVariants && variants.length === 1 && variants[0]?.id === '';

  const [selectedVariant, setSelectedVariant] = useState(variants[0]?.id || '');
  const { addToCart, cartItems, updateQuantity, removeFromCart } = useCart();
  const [addLoading, setAddLoading] = useState(false);
  const [selectedLevel1, setSelectedLevel1] = useState('');
  const [selectedLevel2, setSelectedLevel2] = useState('');
  const [selectedLevel3, setSelectedLevel3] = useState('');
  const [selectedHierarchicalVariant, setSelectedHierarchicalVariant] = useState<HierarchicalVariant | null>(null);

  // Find selected variant object (legacy or hierarchical)
  let selectedVariantObj: any = null;
  let cartItem = null;
  let cartQuantity = 0;
  if (hasHierarchicalVariants) {
    selectedVariantObj = selectedHierarchicalVariant || (hierarchicalVariants && hierarchicalVariants[0]);
    cartItem = selectedVariantObj ? cartItems.find(item => item.variantSku === selectedVariantObj.sku) : null;
    cartQuantity = cartItem?.quantity || 0;
  } else {
    selectedVariantObj = variants.find(v => v.id === selectedVariant) || variants[0];
    cartItem = selectedVariantObj ? cartItems.find(item => item.variantSku === selectedVariantObj.id) : null;
    cartQuantity = cartItem?.quantity || 0;
  }

  const handleAddToCart = async () => {
    if (!selectedVariantObj) return;
    setAddLoading(true);
    if (hasHierarchicalVariants) {
      await addToCart({ ...product, ...selectedVariantObj }, 1, selectedVariantObj.sku);
    } else {
      await addToCart({ ...product, ...selectedVariantObj }, 1, selectedVariantObj.id);
    }
    setAddLoading(false);
  };

  const handleCartQuantityChange = (newQuantity: number) => {
    if (!cartItem) return;
    if (newQuantity <= 0) {
      removeFromCart(cartItem.productId, cartItem.variantSku, cartItem.variantId);
    } else {
      updateQuantity(cartItem.productId, newQuantity, cartItem.variantSku, cartItem.variantId);
    }
  };

  useEffect(() => {
    if (hasHierarchicalVariants) {
      const defaultVariant = hierarchicalVariants!.find(v => v.isDefault) || hierarchicalVariants![0];
      if (defaultVariant) {
        setSelectedLevel1(defaultVariant.attributes[variantHierarchy![0]] || '');
        setSelectedLevel2(defaultVariant.attributes[variantHierarchy![1]] || '');
        setSelectedLevel3(defaultVariant.attributes[variantHierarchy![2]] || '');
        setSelectedHierarchicalVariant(defaultVariant);
      }
    }
  }, [hierarchicalVariants, variantHierarchy, hasHierarchicalVariants]);

  // Option helpers
  const getLevel1Options = () => {
    if (!hasHierarchicalVariants) return [];
    const uniqueValues = new Set<string>();
    hierarchicalVariants!.forEach(variant => {
      const value = variant.attributes[variantHierarchy![0]];
      if (value) uniqueValues.add(value);
    });
    return Array.from(uniqueValues);
  };
  const getLevel2Options = () => {
    if (!hasHierarchicalVariants || !selectedLevel1 || !variantHierarchy || variantHierarchy.length < 2) return [];
    const uniqueValues = new Set<string>();
    hierarchicalVariants!
      .filter(variant => variant.attributes[variantHierarchy![0]] === selectedLevel1)
      .forEach(variant => {
        const value = variant.attributes[variantHierarchy![1]];
        if (value) uniqueValues.add(value);
      });
    return Array.from(uniqueValues);
  };
  const getLevel3Options = () => {
    if (!hasHierarchicalVariants || !selectedLevel1 || !selectedLevel2 || !variantHierarchy || variantHierarchy.length < 3) return [];
    const uniqueValues = new Set<string>();
    hierarchicalVariants!
      .filter(variant => 
        variant.attributes[variantHierarchy![0]] === selectedLevel1 &&
        variant.attributes[variantHierarchy![1]] === selectedLevel2
      )
      .forEach(variant => {
        const value = variant.attributes[variantHierarchy![2]];
        if (value) uniqueValues.add(value);
      });
    return Array.from(uniqueValues);
  };

  // Handlers
  const handleLevel1Change = (value: string) => {
    setSelectedLevel1(value);
    setSelectedLevel2('');
    setSelectedLevel3('');
    setSelectedHierarchicalVariant(null);
    
    // If this is a single-level hierarchy, find and select the variant immediately
    if (variantHierarchy && variantHierarchy.length === 1) {
      const variant = hierarchicalVariants!.find(v =>
        v.attributes[variantHierarchy![0]] === value
      );
      if (variant) {
        setSelectedHierarchicalVariant(variant);
        onVariantSelect?.(variant);
      }
    } else if (variantHierarchy && variantHierarchy.length >= 2) {
      // Auto-select first available level2 option
      const level2Options = new Set<string>();
      hierarchicalVariants!
        .filter(variant => variant.attributes[variantHierarchy![0]] === value)
        .forEach(variant => {
          const levelValue = variant.attributes[variantHierarchy![1]];
          if (levelValue && variant.quantity > 0) level2Options.add(levelValue);
        });
      
      if (level2Options.size > 0) {
        const firstLevel2 = Array.from(level2Options)[0];
        setSelectedLevel2(firstLevel2);
        
        // If this is a 2-level hierarchy, find and select the variant immediately
        if (variantHierarchy.length === 2) {
          const variant = hierarchicalVariants!.find(v =>
            v.attributes[variantHierarchy![0]] === value &&
            v.attributes[variantHierarchy![1]] === firstLevel2
          );
          if (variant) {
            setSelectedHierarchicalVariant(variant);
            onVariantSelect?.(variant);
          }
        } else if (variantHierarchy.length >= 3) {
          // Auto-select first available level3 option
          const level3Options = new Set<string>();
          hierarchicalVariants!
            .filter(variant => 
              variant.attributes[variantHierarchy![0]] === value &&
              variant.attributes[variantHierarchy![1]] === firstLevel2
            )
            .forEach(variant => {
              const levelValue = variant.attributes[variantHierarchy![2]];
              if (levelValue && variant.quantity > 0) level3Options.add(levelValue);
            });
          
          if (level3Options.size > 0) {
            const firstLevel3 = Array.from(level3Options)[0];
            setSelectedLevel3(firstLevel3);
            
            const variant = hierarchicalVariants!.find(v =>
              v.attributes[variantHierarchy![0]] === value &&
              v.attributes[variantHierarchy![1]] === firstLevel2 &&
              v.attributes[variantHierarchy![2]] === firstLevel3
            );
            if (variant) {
              setSelectedHierarchicalVariant(variant);
              onVariantSelect?.(variant);
            }
          }
        }
      }
    }
  };
  const handleLevel2Change = (value: string) => {
    setSelectedLevel2(value);
    setSelectedLevel3('');
    setSelectedHierarchicalVariant(null);
    
    // If this is a 2-level hierarchy, find and select the variant immediately
    if (variantHierarchy && variantHierarchy.length === 2) {
      const variant = hierarchicalVariants!.find(v =>
        v.attributes[variantHierarchy![0]] === selectedLevel1 &&
        v.attributes[variantHierarchy![1]] === value
      );
      if (variant) {
        setSelectedHierarchicalVariant(variant);
        onVariantSelect?.(variant);
      }
    } else if (variantHierarchy && variantHierarchy.length >= 3) {
      // Auto-select first available level3 option
      const level3Options = new Set<string>();
      hierarchicalVariants!
        .filter(variant => 
          variant.attributes[variantHierarchy![0]] === selectedLevel1 &&
          variant.attributes[variantHierarchy![1]] === value
        )
        .forEach(variant => {
          const levelValue = variant.attributes[variantHierarchy![2]];
          if (levelValue && variant.quantity > 0) level3Options.add(levelValue);
        });
      
      if (level3Options.size > 0) {
        const firstLevel3 = Array.from(level3Options)[0];
        setSelectedLevel3(firstLevel3);
        
        const variant = hierarchicalVariants!.find(v =>
          v.attributes[variantHierarchy![0]] === selectedLevel1 &&
          v.attributes[variantHierarchy![1]] === value &&
          v.attributes[variantHierarchy![2]] === firstLevel3
        );
        if (variant) {
          setSelectedHierarchicalVariant(variant);
          onVariantSelect?.(variant);
        }
      }
    }
  };
  const handleLevel3Change = (value: string) => {
    setSelectedLevel3(value);
    const variant = hierarchicalVariants!.find(v =>
      v.attributes[variantHierarchy![0]] === selectedLevel1 &&
      v.attributes[variantHierarchy![1]] === selectedLevel2 &&
      v.attributes[variantHierarchy![2]] === value
    );
    if (variant) {
      setSelectedHierarchicalVariant(variant);
      onVariantSelect?.(variant);
    }
  };

  // Color/non-color split for legacy variants
  const displayVariants = isNonVariantProduct ? [] : variants;
  const colorVariants = displayVariants.filter((variant) => {
    return (variant as any).attributes &&
      Object.keys((variant as any).attributes).some(key =>
        key.toLowerCase().includes('color') || key.toLowerCase().includes('colour'));
  });
  const nonColorVariants = displayVariants.filter((variant) => !colorVariants.includes(variant));

  return {
    hasHierarchicalVariants,
    isNonVariantProduct,
    selectedVariant,
    setSelectedVariant,
    addLoading,
    selectedLevel1,
    setSelectedLevel1,
    selectedLevel2,
    setSelectedLevel2,
    selectedLevel3,
    setSelectedLevel3,
    selectedHierarchicalVariant,
    setSelectedHierarchicalVariant,
    selectedVariantObj,
    cartItem,
    cartQuantity,
    handleAddToCart,
    handleCartQuantityChange,
    getLevel1Options,
    getLevel2Options,
    getLevel3Options,
    handleLevel1Change,
    handleLevel2Change,
    handleLevel3Change,
    colorVariants,
    nonColorVariants,
    displayVariants,
  };
}
